/**
 * NotificationBell (Story 4.19, Task 4)
 *
 * Sidebar notification bell with unread badge and popover dropdown.
 * Polls for unread count every 30s. Shows notification list with
 * role-aware action buttons (AC #9) and mark-all-read functionality.
 */

import { useState, useCallback } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  Bell,
  Warning,
  WarningCircle,
  XCircle,
  CheckCircle,
  Check,
} from "@phosphor-icons/react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from "@provenleap/ui";
import { motion } from "motion/react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../stores/authStore";
import { apiGet, apiPatch } from "../../lib/apiClient";

// ============================================================================
// Types
// ============================================================================

interface NotificationItem {
  id: string;
  category: string;
  type: string;
  level: string;
  title: string;
  message: string;
  provider: string | null;
  tokenId: string | null;
  actionUrl: string | null;
  actionLabel: string | null;
  isRead: boolean;
  createdAt: number; // epoch ms
}

interface UnreadCountResponse {
  data: { count: number };
}

interface NotificationListResponse {
  data: NotificationItem[];
}

// ============================================================================
// Helpers
// ============================================================================

function formatTimeAgo(epochMs: number): string {
  const now = Date.now();
  const diffMs = now - epochMs;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(epochMs));
}

function LevelIcon({ level }: { level: string }) {
  switch (level) {
    case "critical":
      return (
        <WarningCircle
          size={16}
         
          className="text-red-600 shrink-0 mt-0.5"
        />
      );
    case "error":
      return (
        <XCircle
          size={16}

          className="text-red-600 shrink-0 mt-0.5"
        />
      );
    // Story 18.6 AC#9 — goal milestone/achievement notifications carry
    // level "success"; render a distinct emerald CheckCircle so a win never
    // degrades to the default amber Warning triangle (Trap #2).
    case "success":
      return (
        <CheckCircle
          size={16}
          className="text-emerald-600 shrink-0 mt-0.5"
        />
      );
    case "warning":
    default:
      return (
        <Warning
          size={16}
         
          className="text-amber-600 shrink-0 mt-0.5"
        />
      );
  }
}

// ============================================================================
// Component
// ============================================================================

export function NotificationBell() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState<string | null>(null);

  const isOwnerOrAdmin =
    user?.role === "owner" || user?.role === "admin";

  // Unread count — poll every 30s
  const { data: unreadData } = useQuery<number>({
    queryKey: ["notification-unread-count"],
    queryFn: async () => {
      const res = await apiGet("/api/notifications/unread-count");
      if (!res.ok) return 0;
      const json = (await res.json()) as UnreadCountResponse;
      return json.data.count;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

  const unreadCount = unreadData ?? 0;

  // Notification list — fetched when popover opens
  const { data: listData } = useQuery<NotificationItem[]>({
    queryKey: ["notification-list"],
    queryFn: async () => {
      const res = await apiGet("/api/notifications?limit=10");
      if (!res.ok) return [];
      const json = (await res.json()) as NotificationListResponse;
      return json.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled: open,
  });

  const notifications = listData ?? [];

  // Mark all as read mutation
  const markAllRead = useMutation({
    mutationFn: async () => {
      await apiPatch("/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notification-unread-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
    },
  });

  // Dismiss single notification mutation
  const dismissNotification = useMutation({
    mutationFn: async (id: string) => {
      await apiPatch(`/api/notifications/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notification-unread-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
    },
  });

  // Handle reconnect action (opens OAuth popup)
  const handleReconnect = useCallback(
    (actionUrl: string, notificationId: string) => {
      setPopupBlocked(null);
      // Validate actionUrl is a relative path (prevent open-redirect)
      if (!actionUrl.startsWith("/")) return;
      const popup = window.open(actionUrl, "_blank", "width=600,height=700");
      if (!popup || popup.closed) {
        setPopupBlocked(notificationId);
        return;
      }
      // Poll for popup close — dismiss notification on success
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          dismissNotification.mutate(notificationId);
        }
      }, 500);
    },
    [dismissNotification]
  );

  // In-app navigation (e.g. alert notifications → /alerts). Closes the popover
  // and routes within the SPA — NO new window (unlike the OAuth-reconnect flow).
  const handleNavigate = useCallback(
    (actionUrl: string) => {
      if (!actionUrl.startsWith("/")) return; // relative paths only (open-redirect guard)
      setOpen(false);
      navigate({ to: actionUrl });
    },
    [navigate]
  );

  // Mark as read when popover opens
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        setPopupBlocked(null);
      }
    },
    []
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group relative flex items-center justify-center rounded-lg px-2 py-0.5 text-foreground transition-all cursor-pointer"
          aria-label="Notifications"
        >
          <span className="relative shrink-0 mt-1.5">
            <motion.span
              className="inline-flex"
              animate={
                unreadCount > 0
                  ? { rotate: [0, -12, 12, -10, 10, -6, 6, 0], scale: 1.1 }
                  : { rotate: 0, scale: 1 }
              }
              whileHover={{ rotate: [0, -12, 12, -10, 10, -6, 6, 0], scale: 1.1 }}
              transition={
                unreadCount > 0
                  ? {
                      rotate: {
                        duration: 0.9,
                        repeat: Infinity,
                        repeatDelay: 2.6,
                        ease: "easeInOut",
                      },
                      scale: { duration: 0.2 },
                    }
                  : {
                      rotate: { duration: 0.9, ease: "easeInOut" },
                      scale: { duration: 0.2 },
                    }
              }
            >
              <Bell size={18.2} className="text-foreground" />
            </motion.span>
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-red-600 rounded-full">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </span>
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute left-1/2 top-full z-[100] mt-2.5 -translate-x-1/2 transition-all duration-150 ${
              open
                ? "opacity-0 scale-95"
                : "opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:opacity-100 group-focus-visible:scale-100"
            }`}
          >
            <span className="absolute -top-1 left-1/2 z-10 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t border-input bg-background" />
            <span className="relative whitespace-nowrap rounded-md border border-input bg-background px-2.5 py-1 text-[13px] font-medium text-foreground shadow-xs">
              Notifications
            </span>
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="filter-popup-motion w-[300px] p-0 overflow-visible rounded-xl bg-popover shadow-md"
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        {/* Beak — points up at the bell trigger (matches the app's popover panels) */}
        <span
          aria-hidden
          className="absolute -top-[5px] right-5 z-10 h-2.5 w-2.5 rotate-45 bg-popover"
          style={{
            borderTop: "0.5px solid var(--hairline)",
            borderLeft: "0.5px solid var(--hairline)",
          }}
        />
        <div className="overflow-hidden rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-end px-3 py-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg px-2 text-[13px] cursor-pointer"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <Check size={14} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className="max-h-[340px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex min-h-[160px] -translate-y-2 flex-col items-center justify-center">
              <CheckCircle size={32} weight="light" className="mb-2 text-foreground" />
              <p className="text-sm text-foreground">No notifications</p>
            </div>
          ) : (
            <ul className="p-2 space-y-0.5">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className="rounded-lg px-2.5 py-2"
                >
                  <div className="flex gap-2 text-left">
                    <LevelIcon level={n.level} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium leading-tight">
                        {n.title}
                      </p>
                      <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {formatTimeAgo(n.createdAt)}
                      </p>

                      {/* Action button — role-aware (AC #9) */}
                      {n.actionUrl && isOwnerOrAdmin ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 rounded-2xl text-[13px] shadow-xs cursor-pointer transition-all hover:-translate-y-0.5 hover:bg-background hover:shadow-sm"
                            style={{ border: "0.5px solid var(--hairline)" }}
                            onClick={() =>
                              n.category === "alert"
                                ? handleNavigate(n.actionUrl!)
                                : handleReconnect(n.actionUrl!, n.id)
                            }
                          >
                            {n.actionLabel ?? "Reconnect"}
                          </Button>
                          {popupBlocked === n.id && (
                            <p className="text-[11px] text-amber-600 mt-1">
                              Popup blocked — go to Client &gt; Integrations
                              to reconnect
                            </p>
                          )}
                        </>
                      ) : n.actionLabel && !isOwnerOrAdmin ? (
                        <p className="text-[11px] text-muted-foreground italic mt-1">
                          {n.actionLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
